import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import type { Sample } from "@rogoag/shared-ts";
import { SAMPLE_TYPE_LABELS, formatTimestamp } from "./PostSamplingIssues.vm";

export interface SampleDetailsDialogProps {
	open: boolean;
	onClose: () => void;
	samples: Sample[];
	onDownload: () => void;
}

export function SampleDetailsDialog({ open, onClose, samples, onDownload }: SampleDetailsDialogProps) {
	return (
		<Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper" disableScrollLock>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				Sample Details ({samples.length})
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<IconButton onClick={onDownload} size="small" disabled={samples.length === 0}>
						<Download />
					</IconButton>
					<IconButton onClick={onClose} size="small">
						<Close />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				{samples.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
						No samples found.
					</Typography>
				) : (
					<TableContainer>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>Sample ID</TableCell>
									<TableCell>Original Sample ID</TableCell>
									<TableCell>Bag ID</TableCell>
									<TableCell>Order</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Start Depth</TableCell>
									<TableCell>End Depth</TableCell>
									<TableCell>Core Length</TableCell>
									<TableCell># Cores</TableCell>
									<TableCell>Lat</TableCell>
									<TableCell>Lng</TableCell>
									<TableCell>Sampled At</TableCell>
									<TableCell>Scanned At</TableCell>
									<TableCell>Pulled At</TableCell>
									<TableCell>Box UID</TableCell>
									<TableCell>External Ref ID</TableCell>
									<TableCell>Change</TableCell>
									<TableCell>Operator</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{samples.map((sample) => (
									<TableRow key={sample.id}>
										<TableCell>{sample.id}</TableCell>
										<TableCell>{sample.extendedData.sample_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.original_sample_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.bag_id || "—"}</TableCell>
										<TableCell>{sample.extendedData.order || "—"}</TableCell>
										<TableCell>
											{SAMPLE_TYPE_LABELS[sample.extendedData.sample_type as number] ??
												sample.extendedData.sample_type ??
												"—"}
										</TableCell>
										<TableCell>{sample.extendedData.start_depth || "—"}</TableCell>
										<TableCell>{sample.extendedData.end_depth || "—"}</TableCell>
										<TableCell>{sample.extendedData.core_length_cm || "—"}</TableCell>
										<TableCell>{sample.cores?.length ?? 0}</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{sample.coordinates?.[1] ? sample.coordinates[1].toFixed(6) : "—"}
										</TableCell>
										<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
											{sample.coordinates?.[0] ? sample.coordinates[0].toFixed(6) : "—"}
										</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.sampled_at)}</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.scanned_at)}</TableCell>
										<TableCell>{formatTimestamp(sample.extendedData.pulled_at)}</TableCell>
										<TableCell>{sample.extendedData.box_uid || "—"}</TableCell>
										<TableCell>{sample.extendedData.external_reference_id || "—"}</TableCell>
										<TableCell>
											{sample.extendedData.change_type && sample.extendedData.change_type !== "None"
												? `${sample.extendedData.change_type}${sample.extendedData.change_reason ? `: ${sample.extendedData.change_reason}` : ""}`
												: "—"}
										</TableCell>
										<TableCell>{sample.extendedData.operator || "—"}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</DialogContent>
		</Dialog>
	);
}
